import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
    Boxes,
    Cog,
    FileCheck2,
    Inbox,
    PackageCheck,
    Stethoscope,
} from "lucide-react";

import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { WorkshopStatusBadge } from "@/components/common/WorkshopStatusBadge";
import { Card, CardContent } from "@/components/ui/card";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { workshopService } from "@/services/workshop.service";
import { customersService } from "@/services/customers.service";
import { techniciansService } from "@/services/technicians.service";
import {
    WORKSHOP_STATUSES, WORKSHOP_STATUS_LABELS,
} from "@/config/constants";
import { formatDate } from "@/lib/utils";

const STATUS_CARDS = [
    { key: "aguardando_recebimento", label: "Aguardando recebimento", icon: Inbox,
      tone: "text-slate-600 bg-slate-100 dark:bg-slate-900/40 dark:text-slate-300" },
    { key: "recebido", label: "Recebidos", icon: PackageCheck,
      tone: "text-yellow-700 bg-yellow-100 dark:bg-yellow-950/40 dark:text-yellow-300" },
    { key: "em_diagnostico", label: "Em diagnóstico", icon: Stethoscope,
      tone: "text-orange-700 bg-orange-100 dark:bg-orange-950/40 dark:text-orange-300" },
    { key: "aguardando_proposta", label: "Aguardando proposta", icon: FileCheck2,
      tone: "text-indigo-700 bg-indigo-100 dark:bg-indigo-950/40 dark:text-indigo-300" },
];

function KpiCard({ label, value, icon: Icon, tone, testId, onClick, active }) {
    return (
        <Card
            className={`transition ${onClick ? "cursor-pointer hover:border-secondary/60" : ""} ${active ? "border-secondary ring-1 ring-secondary/40" : ""}`}
            onClick={onClick}
            data-testid={testId}
        >
            <CardContent className="flex items-center justify-between p-4">
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {label}
                    </div>
                    <div className="mt-1 text-2xl font-semibold">{value ?? 0}</div>
                </div>
                <div className={`grid h-10 w-10 place-items-center rounded-lg ${tone}`}>
                    <Icon className="h-5 w-5" />
                </div>
            </CardContent>
        </Card>
    );
}

export default function WorkshopListPage() {
    const navigate = useNavigate();

    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
    const [customerId, setCustomerId] = useState("");
    const [technicianId, setTechnicianId] = useState("");

    const dashboard = useQuery({
        queryKey: ["workshop-dashboard"],
        queryFn: () => workshopService.dashboard(),
        refetchInterval: 30000,
    });

    const customers = useQuery({
        queryKey: ["customers-lite"],
        queryFn: () => customersService.list({ pageSize: 100 }),
    });
    const technicians = useQuery({
        queryKey: ["technicians-lite"],
        queryFn: () => techniciansService.list({ pageSize: 100 }),
    });

    const list = useQuery({
        queryKey: ["workshops", { page, search, status, customerId, technicianId }],
        queryFn: () => workshopService.list({
            page, pageSize: 20, search,
            status: status || undefined,
            customer_id: customerId || undefined,
            technician_id: technicianId || undefined,
            sortBy: "created_at", sortOrder: "desc",
        }),
    });

    const columns = [
        {
            key: "work_order_number", header: "OS", sortable: false,
            cell: (r) => (
                <div className="flex items-center gap-3">
                    <div className="grid h-8 w-8 place-items-center rounded-md bg-muted text-muted-foreground">
                        <Cog className="h-4 w-4" />
                    </div>
                    <div>
                        <div className="font-mono text-sm font-medium">{r.work_order_number}</div>
                        <div className="text-xs text-muted-foreground">Oficina · {r.id?.slice(0, 8)}</div>
                    </div>
                </div>
            ),
        },
        {
            key: "asset", header: "Equipamento",
            cell: (r) => (
                <div>
                    <div className="font-mono text-xs">{r.asset_snapshot_internal_code || "—"}</div>
                    <div className="text-xs text-muted-foreground">
                        {r.asset_snapshot_category}
                        {r.asset_snapshot_manufacturer ? ` · ${r.asset_snapshot_manufacturer}` : ""}
                        {r.asset_snapshot_model ? ` · ${r.asset_snapshot_model}` : ""}
                    </div>
                </div>
            ),
        },
        {
            key: "serial", header: "Nº Série",
            cell: (r) => <span className="text-xs">{r.asset_snapshot_serial_number || "—"}</span>,
        },
        {
            key: "workshop_status", header: "Status",
            cell: (r) => <WorkshopStatusBadge value={r.workshop_status} />,
        },
        {
            key: "arrival_date", header: "Chegada",
            cell: (r) => (
                <span className="text-xs text-muted-foreground">
                    {r.arrival?.arrival_date ? formatDate(r.arrival.arrival_date) : "—"}
                </span>
            ),
        },
        {
            key: "created_at", header: "Enviado em",
            cell: (r) => (
                <span className="text-xs text-muted-foreground">{r.created_at ? formatDate(r.created_at) : "—"}</span>
            ),
        },
    ];

    const counts = dashboard.data?.counts || {};

    const filtersActive = [];
    if (status) filtersActive.push(`Status: ${WORKSHOP_STATUS_LABELS[status] || status}`);
    if (customerId) filtersActive.push("Cliente filtrado");
    if (technicianId) filtersActive.push("Técnico filtrado");

    return (
        <div className="space-y-6" data-testid="workshop-list-page">
            <PageHeader
                title="Oficina"
                description="Recepção, diagnóstico e laudo técnico dos equipamentos em bancada."
                breadcrumb={[{ label: "Início", to: "/dashboard" }, { label: "Oficina" }]}
            />

            {/* KPI cards — clique para filtrar */}
            <div className="grid gap-3 md:grid-cols-4">
                {STATUS_CARDS.map((c) => (
                    <KpiCard
                        key={c.key}
                        label={c.label}
                        value={counts[c.key]}
                        icon={c.icon}
                        tone={c.tone}
                        testId={`workshop-kpi-${c.key}`}
                        active={status === c.key}
                        onClick={() => { setPage(1); setStatus(status === c.key ? "" : c.key); }}
                    />
                ))}
            </div>

            <DataTable
                dataTestId="workshop"
                columns={columns}
                query={list.data}
                loading={list.isLoading}
                onSearch={(t) => { setPage(1); setSearch(t); }}
                onPageChange={setPage}
                onRowClick={(r) => navigate(`/workshop/${r.id}`)}
                emptyState={{
                    title: "Nenhum equipamento em oficina",
                    description:
                        "Envie uma OS em atendimento para a oficina a partir dos detalhes da OS (botão Finalizar OS → Enviar para Oficina).",
                }}
                filtersActive={filtersActive}
                toolbarLeft={
                    <>
                        <Select
                            value={status || "__all__"}
                            onValueChange={(v) => { setPage(1); setStatus(v === "__all__" ? "" : v); }}
                        >
                            <SelectTrigger className="h-9 w-56" data-testid="workshop-status-filter">
                                <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all__">Todos status</SelectItem>
                                {WORKSHOP_STATUSES.map((s) => (
                                    <SelectItem key={s.value} value={s.value}>
                                        <span className="flex items-center gap-2">
                                            <span className={`inline-block h-2 w-2 rounded-full ${s.dotClass}`} />
                                            {s.label}
                                        </span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select
                            value={customerId || "__all__"}
                            onValueChange={(v) => { setPage(1); setCustomerId(v === "__all__" ? "" : v); }}
                        >
                            <SelectTrigger className="h-9 w-56" data-testid="workshop-customer-filter">
                                <SelectValue placeholder="Cliente" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all__">Todos clientes</SelectItem>
                                {(customers.data?.items || []).map((c) => (
                                    <SelectItem key={c.id} value={c.id}>{c.legal_name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select
                            value={technicianId || "__all__"}
                            onValueChange={(v) => { setPage(1); setTechnicianId(v === "__all__" ? "" : v); }}
                        >
                            <SelectTrigger className="h-9 w-56" data-testid="workshop-technician-filter">
                                <SelectValue placeholder="Técnico" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all__">Todos técnicos</SelectItem>
                                {(technicians.data?.items || []).map((t) => (
                                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </>
                }
            />

            {/* Recentes (últimos 6) */}
            {(dashboard.data?.recent || []).length > 0 && (
                <div>
                    <div className="mb-2 flex items-center gap-2">
                        <Boxes className="h-4 w-4 text-muted-foreground" />
                        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Enviados recentemente
                        </div>
                    </div>
                    <div className="grid gap-2 md:grid-cols-3">
                        {(dashboard.data.recent || []).map((r) => (
                            <button
                                key={r.id}
                                type="button"
                                onClick={() => navigate(`/workshop/${r.id}`)}
                                className="group flex items-center gap-3 rounded-md border bg-card p-3 text-left transition hover:border-secondary/60"
                                data-testid={`workshop-recent-${r.id}`}
                            >
                                <div className="grid h-9 w-9 place-items-center rounded-md bg-muted text-muted-foreground">
                                    <Cog className="h-4 w-4" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="truncate font-mono text-sm font-medium">{r.work_order_number}</div>
                                    <div className="truncate text-xs text-muted-foreground">
                                        {r.asset_snapshot_internal_code} · {r.asset_snapshot_manufacturer || ""} {r.asset_snapshot_model || ""}
                                    </div>
                                </div>
                                <WorkshopStatusBadge value={r.workshop_status} />
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
